/**
 * Failure reasons arrive as free-text Arabic strings (50+ distinct values in
 * a one-month window, from canonical short reasons like "لا يرد" to full
 * colloquial sentences). There is no normalized reason key in the payload —
 * reasonCode families (PP001-*, C0001-*) exist per attempt but not on the
 * analytics buckets — so categories are derived from the strings actually
 * observed in prod (M-54). First matching rule wins; order matters:
 * e.g. "لا يرد بعد التأجيل" must land in unreachable, not postponed, and
 * "رفض بسبب اختلاف المنتج" in mismatch, not refused.
 */
export type ReasonCategoryKey =
  | 'unreachable'
  | 'refused'
  | 'postponed'
  | 'mismatch'
  | 'address'
  | 'duplicate'
  | 'other';

const CATEGORY_RULES: Array<[ReasonCategoryKey, RegExp]> = [
  ['duplicate', /مكرر|مستلم سابق/],
  [
    'mismatch',
    /غير مطابق|اختلاف المنتج|عدم الفحص|بسبب السعر|بسبب الحجم|بسبب النوع|نقص الطلب/,
  ],
  [
    'unreachable',
    /لا ?يرد|لايرد|الاتصال|الهاتف|مغلق|خطأ في الرقم|الرقم غير معروف|مكالم|ما يتحصل/,
  ],
  ['address', /عنوان|محافظة|تحويل|دهوك|اربيل|أربيل/],
  [
    'postponed',
    /اجيل|أجيل|مؤجل|مسافر|غير متواجد|يواعد|غدا|غدأ|جمعه|جمعة|راجع|حاظرني/,
  ],
  ['refused', /رفض|ملغى|ملغي|لاغي|إلغاء|الغاء|ميريده|غير جاد|كذاب/],
];

// Strip Arabic tashkeel (e.g. "مُلغى" → "ملغى") and tatweel (ـ, U+0640)
// so diacritics or elongation in prod free-text never defeat a rule.
const stripDiacritics = (s: string): string =>
  s.replace(/[\u0640\u064B-\u0652\u0670]/g, '');

export const categorizeReason = (reason: string): ReasonCategoryKey => {
  const normalized = stripDiacritics((reason || '').trim());

  for (const [key, pattern] of CATEGORY_RULES) {
    if (pattern.test(normalized)) {
      return key;
    }
  }

  return 'other';
};

export const REASON_CATEGORY_MESSAGE_IDS: Record<ReasonCategoryKey, string> = {
  unreachable: 'failedOrders.category.unreachable',
  refused: 'failedOrders.category.refused',
  postponed: 'failedOrders.category.postponed',
  mismatch: 'failedOrders.category.mismatch',
  address: 'failedOrders.category.address',
  duplicate: 'failedOrders.category.duplicate',
  other: 'failedOrders.category.other',
};

export const REASON_CATEGORY_DEFAULTS: Record<ReasonCategoryKey, string> = {
  unreachable: 'Customer unreachable',
  refused: 'Refused / cancelled',
  postponed: 'Postponed / away',
  mismatch: 'Product or order issue',
  address: 'Address problem',
  duplicate: 'Duplicate order',
  other: 'Other',
};
